import { API } from "@/api"
import { HttpAPIError, ImgCodeRes, WallpaperThemeRes } from "@/api/model"
import { getAppConfig } from "@/config/env"
import {
  AuthHeader,
  BizError,
  BizResult,
  DeviceStatus,
  HttpBizError,
  KcLoginParam,
  LoginType,
  Ok,
  RemoteDeskInfo,
  User,
  userDisplayName,
} from "@/models/biz_model"
import {
  ERR_ACCOUNT_PASS_ERROR,
  ERR_AUTO_LOGIN_DISABLED_BY_ADMIN,
  ERR_DEVICE_SECRET_UNAVAILABLE,
  ERR_FACE_LOGIN_DISABLED_BY_ADMIN,
  ERR_LOGIN_TICKET_EXPIRED,
  ERR_AGENT_OFFLINE,
  ERR_CAPTCHA_NOT_EMPTY,
  ERR_CAPTCHA_VERIFY_API_REQ,
  ERR_DEV_LOCK,
  ERR_DEV_STATUS_EXCEPTION,
  ERR_DEV_STATUS_NOT_FOUND,
  ERR_INPUT_VERIFY_CODE_API_REQ,
  ERR_KS_AUTH_CODE_EXPIRED,
  ERR_KS_AUTH_CODE_INVALID,
  ERR_LOGIN_TOO_MANY_TIMES,
  ERR_NO_DEVICE,
  ERR_SET_PHONE_API_REQ,
  ERR_VERIFY_CODE_OR_PHONE_FAILED,
  ERR_WEAK_PASSWORD,
  SET_PASSWORD_ALREADY_SET,
  SET_PASSWORD_PARAM_EMPTY,
  SET_PASSWORD_TOKEN_EXPIRED,
  SET_PASSWORD_TOKEN_INVALID,
  SET_PASSWORD_USER_NOT_FOUND,
} from "@/models/constants"

import { AuthCache, AutoLoginCache, UserCache } from "@/utils/storage"
import { DataReportUsecase } from "./data_report"
import { ClientConfigUseCase } from "./client_config"
import { store } from "@/store"
import {
  selectFpHash,
  setAuth,
  setLoginPwd,
  setPhoneNum,
  SetPhoneOrVerifyCode,
  setSetPhoneInfo,
  setSetPhoneOrVerifyCode,
} from "@/stores/auth_slice"
import {
  changeViewType,
  LoginStatus,
  setImgCodeObj,
  setKcLoginParams,
  setShowImgCodeInPhone,
  updateLoginStatus,
  ViewType,
} from "@/stores/login_slice"
import { encryptToVaultFormat } from "@/utils/tools"
import { DeviceUseCase } from "./device"

// 当前登录的用户
let curUser: User | null = null

interface oldLoginRes {
  userObj: User
}
interface newLoginRes {
  userObj: User
  devicesList: RemoteDeskInfo[]
  needChangePassword: boolean
  changePasswordMessage: string
}

// 公共：处理登录错误逻辑
async function processLoginError(
  res: HttpAPIError,
  password: string,
  loginTag: string
): Promise<BizResult<never>> {
  // 企业登录: token已过期或token无效，跳转到授权页，重新授权
  if (
    res.code === ERR_KS_AUTH_CODE_EXPIRED ||
    res.code === ERR_KS_AUTH_CODE_INVALID
  ) {
    const { baseAPIUrl, siteUrl } = getAppConfig()
    if (location.hostname === "web-game.ked.ksyun.com") {
      window.location.href = `${baseAPIUrl}/wps_ksyun/login?ver=v2&redirect_uri=https://web-game.ked.ksyun.com/login`
    } else {
      window.location.href = `${baseAPIUrl}/wps_ksyun/login?ver=v2&redirect_uri=${siteUrl}/login`
    }
    return HttpBizError.stayHttpError(res)
  }

  // 没有注册手机号，设置手机号 -10008； 有注册手机号,输入验证码 -10009
  if (
    res.code === ERR_SET_PHONE_API_REQ ||
    res.code === ERR_INPUT_VERIFY_CODE_API_REQ
  ) {
    if (loginTag === "KS") {
      store.dispatch(setLoginPwd(password))
      store.dispatch(changeViewType(ViewType.setPhoneVerify))
      store.dispatch(
        setSetPhoneInfo({ phoneTail: "", smsToken: "", countDown: 0 })
      )

      if (res.code === ERR_SET_PHONE_API_REQ) {
        store.dispatch(setSetPhoneOrVerifyCode(SetPhoneOrVerifyCode.setPhone))
      }

      if (res.code === ERR_INPUT_VERIFY_CODE_API_REQ) {
        store.dispatch(
          setSetPhoneOrVerifyCode(SetPhoneOrVerifyCode.inputVerifyCode)
        )
        store.dispatch(
          setSetPhoneInfo({
            phoneTail: res?.data?.phone,
            smsToken: res?.data?.smsToken,
            countDown: 60,
          })
        )
      }
    } else if (loginTag === "Account") {
      if (res.code === ERR_SET_PHONE_API_REQ) {
        return HttpBizError.stayHttpError(res)
      }

      if (res.code === ERR_INPUT_VERIFY_CODE_API_REQ) {
        store.dispatch(setLoginPwd(password))
        store.dispatch(changeViewType(ViewType.setPhoneVerify))
        store.dispatch(
          setSetPhoneInfo({ phoneTail: "", smsToken: "", countDown: 0 })
        )

        store.dispatch(
          setSetPhoneOrVerifyCode(SetPhoneOrVerifyCode.inputVerifyCode)
        )
        console.log("loginTag2:", loginTag)

        store.dispatch(
          setSetPhoneInfo({
            phoneTail: res?.data?.phone,
            smsToken: res?.data?.smsToken,
            countDown: 60,
          })
        )
      }
    }

    return HttpBizError.stayHttpError(res)
  }

  // 短信验证码校验失败，60秒后重新发送验证码 -10021
  if (res.code === ERR_VERIFY_CODE_OR_PHONE_FAILED) {
    const {
      phoneTail: currentPhoneTail,
      smsToken: currentSmsToken,
      countDown: currentCountDown,
    } = store.getState().auth.setPhoneInfo

    store.dispatch(changeViewType(ViewType.setPhoneVerify))
    store.dispatch(
      setSetPhoneInfo({
        phoneTail: res?.data?.phone ?? currentPhoneTail,
        smsToken: res?.data?.smsToken ?? currentSmsToken,
        countDown: currentCountDown,
      })
    )
    store.dispatch(
      setSetPhoneOrVerifyCode(SetPhoneOrVerifyCode.inputVerifyCode)
    )
    return HttpBizError.stayHttpError(res)
  }

  // 手机验证码发送过于频繁，后端返回：倒计时60秒，保留后端msg  -10022
  if (res.code === -10022) {
    return HttpBizError.stayHttpError(res)
  }

  // 用户未激活，请先激活账号
  if (res.code === -10023) {
    return HttpBizError.stayHttpError(res)
  }

  // 自动登录相关错误由服务端返回最终提示文案，不能被客户端固定文案覆盖。
  if (
    [
      ERR_LOGIN_TICKET_EXPIRED,
      ERR_DEVICE_SECRET_UNAVAILABLE,
      ERR_AUTO_LOGIN_DISABLED_BY_ADMIN,
      ERR_FACE_LOGIN_DISABLED_BY_ADMIN,
    ].includes(res.code)
  ) {
    return HttpBizError.stayHttpError(res)
  }

  // code为7 保留后端返回的msg
  if (res.code === 7) {
    return HttpBizError.stayHttpError(res)
  }

  // 登录失败次数过多，请 5 分钟后再试  -10001
  // 登录失败次数10次以上，锁定账户，并直接使用接口返回的错误信息
  if (res.code === ERR_LOGIN_TOO_MANY_TIMES) {
    return HttpBizError.stayHttpError(res)
  }

  // -10007:  后端返回：您当前密码强度较弱，请点击【重置密码】进行修改
  if (res.code === ERR_WEAK_PASSWORD) {
    return HttpBizError.stayHttpError(res)
  }

  // 隐私相关：
  // -10025:  后端返回：当前设备不是可信设备，无法登录
  // -10801 : 登录设备不存在
  // -10802 : 可信设备验证码为空或错误
  // -10803 : 该配置受管理员控制，无法修改
  // -10804 : 请至少设置信任一台设备后再启用仅信任设备登录
  // -10811 : 通知已读/未读缺少参数
  // -10812 : 通知列表参数错误
  if (
    res.code === -10025 ||
    res.code === -10801 ||
    res.code === -10802 ||
    res.code === -10803 ||
    res.code === -10804 ||
    res.code === -10811 ||
    res.code === -10812
  ) {
    return HttpBizError.stayHttpError(res)
  }

  // 图片验证码相关：登录失败次数超过5次，展示图片验证码
  // 图片验证码错误 -10016
  // 图片验证码不能为空（密码错误超过5次） -10017
  if (
    res.code === ERR_CAPTCHA_VERIFY_API_REQ ||
    res.code === ERR_CAPTCHA_NOT_EMPTY
  ) {
    store.dispatch(changeViewType(ViewType.imgcodeVerify))
    // 清空图片校验码，图片校验码只能使用一次
    store.dispatch(setImgCodeObj({ captchaId: "", captcha: "" }))

    // -10017: 此时要在手机验证码页面，显示图片验证码
    if (res.code === ERR_CAPTCHA_NOT_EMPTY) {
      store.dispatch(setShowImgCodeInPhone(true))
      // 清空图片校验码，图片校验码只能使用一次
      store.dispatch(setImgCodeObj({ captchaId: "", captcha: "" }))
    }

    // 刷新图片验证码
    const getImgCodeRes = await getImgCode()
    if (getImgCodeRes instanceof BizError) {
      store.dispatch(
        updateLoginStatus({
          msg: getImgCodeRes.displayErrorMsg,
          status: LoginStatus.error,
        })
      )
      return BizError.fromHttpError(res)
    }

    const { picPath, captchaId } = getImgCodeRes.unwrap()
    store.dispatch(setImgCodeObj({ captcha: picPath, captchaId }))

    return BizError.fromHttpError(res)
  }

  // 登录失败，请检查账号或密码（回到登录页重新输入密码） -10006
  if (res.code === ERR_ACCOUNT_PASS_ERROR) {
    store.dispatch(changeViewType(ViewType.login))
    return BizError.fromHttpError(res)
  }

  return BizError.fromHttpError(res)
}

function findCachedLoginUser(username: string): User | null {
  return (
    UserCache.getUser(username) ??
    UserCache.getUsers().find(
      (user) => user.account === username || user.loginAccount === username
    ) ??
    null
  )
}

function createLoginReportData(
  username: string,
  loginTag: string,
  responseData?: any
) {
  const cachedUser = findCachedLoginUser(username)
  const responseUser = responseData?.user ?? responseData
  const responseUserName =
    typeof responseUser?.userName === "string" ? responseUser.userName : ""
  const responseNickName =
    typeof responseUser?.nickName === "string" ? responseUser.nickName : ""

  if (responseUserName || responseNickName) {
    const user: User = {
      account: loginTag === "KS" ? username : responseUserName || username,
      loginAccount: username,
      nickName: responseNickName || cachedUser?.nickName || "",
      avatar: "",
      loginType: loginTag as LoginType,
      ksAuthCode: cachedUser?.ksAuthCode,
      uid: cachedUser?.uid,
      business_scene: cachedUser?.business_scene,
      region: cachedUser?.region,
      localDevId: cachedUser?.localDevId,
      desktopId: cachedUser?.desktopId,
      deviceId: cachedUser?.deviceId,
    }
    return {
      user: user.account,
      nickName: userDisplayName(user),
      localDevId: user.localDevId,
      desktopId: user.desktopId,
      deviceId: user.deviceId,
      businessScene: user.business_scene,
      region: user.region,
    }
  }

  if (cachedUser !== null) {
    return {
      user: cachedUser.account,
      nickName: userDisplayName(cachedUser),
      localDevId: cachedUser.localDevId,
      desktopId: cachedUser.desktopId,
      deviceId: cachedUser.deviceId,
      businessScene: cachedUser.business_scene,
      region: cachedUser.region,
    }
  }

  return { user: username }
}

// 公共：保存用户与授权信息
function saveUserAndAuth(
  res: any,
  options: {
    username: string
    loginTag?: string
    ksAuthCode?: string
    uid?: string
    region?: string
    businessScene?: string
    localDevId?: string
  }
): User {
  const {
    username,
    loginTag,
    ksAuthCode,
    uid,
    region,
    businessScene,
    localDevId,
  } = options
  const user: User = {
    account: loginTag === "KS" ? username : res?.user?.userName,
    loginAccount: username,
    nickName: res?.user?.nickName,
    avatar: "",
    loginType: loginTag as LoginType,
    ksAuthCode,
    uid,
    // 用于数据上报的字段
    accountID: res?.user?.AccountID,
    business_scene: businessScene || res?.list?.[0]?.info?.business_scene || "",
    region: region || res?.list?.[0]?.regionName || "",
    localDevId: localDevId == null ? "" : String(localDevId),
    desktopId: res?.list?.[0]?.desktopID,
    deviceId:
      res?.list?.[0]?.deviceID != null ? String(res.list[0].deviceID) : "",
  }
  // 设置当前用户（含触发动态配置拉取，对齐 android _onLogin）
  setCurrentUser(user)
  UserCache.saveUser(user)
  UserCache.setCurrentUser(user)

  DataReportUsecase.setUserInfo(user)

  const auth: AuthHeader = {
    token: res.token,
    companyUniqID: res.companyUniqID?.toString() ?? "",
  }
  store.dispatch(setAuth(auth))
  AuthCache.saveAuth(user.account, auth)

  return user
}

/** 使用当前浏览器已保存的授权令牌和设备密钥进行免密码登录。 */
async function autoLogin(
  user: User,
  fpHash: string,
  phone?: string,
  smsCode?: string,
  smsToken?: string
): Promise<BizResult<newLoginRes>> {
  const auth = AuthCache.getAuth(user.account)
  const config = AutoLoginCache.getConfig(user.account)
  if (
    !auth ||
    !config.enabled ||
    !config.deviceSecret ||
    config.disabledByAdmin
  ) {
    return new BizError(ERR_LOGIN_TICKET_EXPIRED)
  }

  const res = await API.deviceSecretLogin(
    user.account,
    auth.token,
    config.deviceSecret,
    fpHash,
    phone,
    smsCode,
    smsToken
  )
  if (res instanceof HttpAPIError) {
    if (res.code === ERR_LOGIN_TICKET_EXPIRED) {
      AuthCache.deleteAuth(user.account)
      AutoLoginCache.updateConfig(user.account, { enabled: false })
    }
    if (res.code === ERR_DEVICE_SECRET_UNAVAILABLE) {
      AutoLoginCache.updateConfig(user.account, {
        enabled: false,
        deviceSecret: undefined,
      })
    }
    if (res.code === ERR_AUTO_LOGIN_DISABLED_BY_ADMIN) {
      AutoLoginCache.updateConfig(user.account, {
        enabled: false,
        disabledByAdmin: true,
      })
    }
    return await processLoginError(res, "", user.loginType)
  }

  const savedUser = saveUserAndAuth(res, {
    username: user.account,
    loginTag: user.loginType,
    ksAuthCode: user.ksAuthCode,
    uid: user.uid,
  })
  store.dispatch(setPhoneNum(res?.user?.phone ?? ""))

  let devices: RemoteDeskInfo[]
  if (Array.isArray(res.list) && res.list.length > 0) {
    devices = res.list.map((dev) => ({
      id: dev.deviceID,
      desktopId: dev.desktopID,
      name: dev.deviceName,
      serverPrivateIp: dev.serverPrivateIp,
      gateWayIp: dev.gateWayIP,
      desktopPublicIp: dev.desktopPublicIp,
      idcId: dev.node,
      status: dev.status as DeviceStatus,
      online: dev.online,
      businessStatus: dev.businessStatus,
      canRestart: dev.canRestart,
    }))
  } else {
    const devicesRes = await DeviceUseCase.getDevices()
    if (devicesRes instanceof BizError) return devicesRes
    devices = devicesRes.unwrap()
  }

  if (devices.length === 0) return new BizError(ERR_NO_DEVICE)
  return new Ok({
    userObj: savedUser,
    devicesList: devices,
    needChangePassword: res.needChangePassword ?? false,
    changePasswordMessage: res.changePasswordMessage ?? "",
  })
}

async function setAutoLogin(
  user: User,
  fpHash: string,
  enable: boolean
): Promise<BizResult<void>> {
  const config = AutoLoginCache.getConfig(user.account)
  if (enable && config.disabledByAdmin) {
    return new BizError(ERR_AUTO_LOGIN_DISABLED_BY_ADMIN)
  }

  if (enable && !config.deviceSecret) {
    const res = await API.createDeviceSecret(fpHash, 2)
    if (res instanceof HttpAPIError) return BizError.fromHttpError(res)
    AutoLoginCache.saveConfig(user.account, {
      ...config,
      enabled: true,
      deviceSecret: res.deviceSecret,
    })
    return new Ok(undefined)
  }

  AutoLoginCache.updateConfig(user.account, { enabled: enable })
  return new Ok(undefined)
}

/**
 * 账号登录（用老的登录接口,用来重启/关机云电脑时校验密码）
 */
async function oldAccountLogin(
  username: string,
  password: string,
  loginTag: string,
  captcha?: string,
  captchaId?: string,
  ksAuthCode?: string,
  phone?: string,
  smsCode?: string,
  smsToken?: string
): Promise<BizResult<oldLoginRes>> {
  const cachedUser = findCachedLoginUser(username)
  // 上报登录事件
  DataReportUsecase.reportLog("oldLogin", {
    user: cachedUser?.account ?? username,
    nickName: cachedUser ? userDisplayName(cachedUser) : "",
  })

  // const fpHash = useAppSelector(selectFpHash)

  // 对密码进行加密
  const publicKeyObj = store.getState().login.publicKeyObj
  let vaultCiphertext = ""
  try {
    vaultCiphertext = await encryptToVaultFormat(password, publicKeyObj)
  } catch (error) {
    console.error("密码加密失败:", error)
  }

  // 登录请求
  const res = await API.oldLogin(
    username,
    vaultCiphertext, // password加密为vault格式
    // password, // password加密为vault格式
    // fpHash, // fingerprintId
    captcha,
    captchaId,
    ksAuthCode,
    phone,
    smsCode,
    smsToken
  )

  if (res instanceof HttpAPIError) {
    DataReportUsecase.reportError(
      res.code,
      res.msg,
      createLoginReportData(username, loginTag, res.data)
    )
    console.error("UserUseCase account login error: ", res)
    return await processLoginError(res, password, loginTag)
  }

  // 保存用户信息，并设置为当前用户
  // const userInfo = saveUserAndAuth(res, {
  saveUserAndAuth(res, {
    username,
    loginTag,
    ksAuthCode,
    uid: cachedUser?.uid,
  })

  // 设备列表（旧的，用来获取上报数据的region，后面这个字段会在 新登录接口中拿）（后续不要）
  const oldDeviceRes = await API.getDeviceListOld()
  if (oldDeviceRes instanceof HttpAPIError) {
    DataReportUsecase.reportError(
      oldDeviceRes.code,
      oldDeviceRes.msg,
      createLoginReportData(username, loginTag, oldDeviceRes.data)
    )
    console.error("UserUseCase oldDeviceRes error: ", oldDeviceRes)
    return BizError.fromHttpError(oldDeviceRes)
  }

  // 获取本地设备ID（用于上报数据）
  const devLocalIdRes = await DeviceUseCase.getLocalDeviceId()
  if (devLocalIdRes instanceof BizError) {
    return devLocalIdRes
  }
  const localDevId = devLocalIdRes.unwrap()

  // 拿到region后，再次保存用户信息，并设置为当前用户（后续不要）
  const userRegion = saveUserAndAuth(res, {
    username,
    loginTag,
    ksAuthCode,
    uid: cachedUser?.uid,
    region: oldDeviceRes?.[0]?.region,
    businessScene: oldDeviceRes?.[0]?.info?.business_scene || "",
    localDevId,
  })

  return new Ok({ userObj: userRegion })
}

/**
 * 账号登录（用新的登录接口）
 */
async function newAccountLogin(
  username: string,
  password: string,
  loginTag: string,
  captcha?: string,
  captchaId?: string,
  ksAuthCode?: string,
  phone?: string,
  smsCode?: string,
  smsToken?: string
): Promise<BizResult<newLoginRes>> {
  const cachedUser = findCachedLoginUser(username)

  const fpHash = selectFpHash(store.getState())

  // 对密码进行加密
  const publicKeyObj = store.getState().login.publicKeyObj
  let vaultCiphertext = ""
  try {
    vaultCiphertext = await encryptToVaultFormat(password, publicKeyObj)
  } catch (error) {
    console.error("密码加密失败:", error)
  }

  // 登录请求
  const res = await API.newLogin(
    username,
    vaultCiphertext, // password加密为vault格式
    // password,
    captcha,
    captchaId,
    ksAuthCode,
    phone,
    smsCode,
    smsToken,
    fpHash // 浏览器指纹
  )

  if (res instanceof HttpAPIError) {
    DataReportUsecase.reportError(
      res.code,
      res.msg,
      createLoginReportData(username, loginTag, res.data)
    )
    console.error("UserUseCase newAccountLogin error: ", res)
    return await processLoginError(res, password, loginTag)
  }

  if (res?.user?.phone) {
    store.dispatch(setPhoneNum(res.user.phone))
  }

  // 保存用户信息，并设置为当前用户
  // const userInfo = saveUserAndAuth(res, {
  saveUserAndAuth(res, {
    username,
    loginTag,
    ksAuthCode,
    uid: cachedUser?.uid,
  })
  // 上报登录成功事件，使用登录接口返回后的标准账号和昵称。
  DataReportUsecase.reportLog("newLogin")

  // 先保存用户信息，保存auth，用auth去请求设备列表（旧接口），拿到region后，再次保存用户信息（后续不要，后续的region直接从新登录接口中拿）
  // 设备列表（旧的，用来获取上报数据的region，后面这个字段会在 新登录接口中拿）（后续不要）
  const oldDeviceRes = await API.getDeviceListOld()
  if (oldDeviceRes instanceof HttpAPIError) {
    DataReportUsecase.reportError(
      oldDeviceRes.code,
      oldDeviceRes.msg,
      createLoginReportData(username, loginTag, oldDeviceRes.data)
    )
    console.error("UserUseCase oldDeviceRes error: ", oldDeviceRes)
    return BizError.fromHttpError(oldDeviceRes)
  }

  // 获取本地设备ID（用于上报数据）
  const devLocalIdRes = await DeviceUseCase.getLocalDeviceId()
  if (devLocalIdRes instanceof BizError) {
    return devLocalIdRes
  }
  const localDevId = devLocalIdRes.unwrap()

  // 拿到region之后再次 保存用户信息，并设置为当前用户（后续不要）
  const userRegion = saveUserAndAuth(res, {
    username,
    loginTag,
    ksAuthCode,
    uid: cachedUser?.uid,
    region: oldDeviceRes?.[0]?.region,
    businessScene: oldDeviceRes?.[0]?.info?.business_scene || "",
    localDevId,
  })

  const devices: RemoteDeskInfo[] = []
  for (const dev of res.list) {
    if (!dev) {
      DataReportUsecase.reportError(
        ERR_DEV_STATUS_NOT_FOUND,
        `获取设备状态失败-未返回设备状态和设备列表`
      )
      console.error(`UserUsecase cannot find device status and deviceList`)
      return new BizError(ERR_DEV_STATUS_NOT_FOUND)
    }

    if (dev.businessStatus === "Lock") {
      DataReportUsecase.reportError(ERR_DEV_LOCK, `机器欠费 已被锁定`)
      return new BizError(ERR_DEV_LOCK)
    }

    if (dev.status === "Pending") {
      DataReportUsecase.reportError(
        ERR_DEV_STATUS_EXCEPTION,
        `获取设备状态失败-机器异常`,
        {
          desktopId: dev.desktopID,
          deviceId: String(dev.deviceID),
        }
      )
      // 机器状态异常
      return new BizError(ERR_DEV_STATUS_EXCEPTION)
    }

    if (dev.status === "Running" && !dev.online) {
      // 上报远端设备不在线
      DataReportUsecase.reportLog("signal_agent_online_err", {
        desktopId: dev.desktopID,
        deviceId: String(dev.deviceID),
      })
      console.error(`UserUsecase desktop ${dev.desktopID} agent offline`)
      return new BizError(ERR_AGENT_OFFLINE)
    }

    // 修正 gatewayIp, privateServerIp, status 字段
    const deviceInfo = {
      id: dev.deviceID,
      desktopId: dev.desktopID,
      name: dev.deviceName,
      serverPrivateIp: dev.serverPrivateIp,
      gateWayIp: dev.gateWayIP,
      desktopPublicIp: dev.desktopPublicIp,
      idcId: dev.node,
      status: dev.status as DeviceStatus,
      online: dev.online,
      businessStatus: dev.businessStatus,
      canRestart: dev.canRestart,
    }
    devices.push(deviceInfo)
  }

  return new Ok({
    userObj: userRegion,
    devicesList: devices,
    needChangePassword: res.needChangePassword ?? false,
    changePasswordMessage: res.changePasswordMessage ?? "",
  })
}

// 开始企业登录
async function startKSLogin(): Promise<BizResult<number>> {
  // 跳转到企业登录页面
  const { baseAPIUrl, siteUrl } = getAppConfig()

  if (location.hostname === "web-game.ked.ksyun.com") {
    window.location.href = `${baseAPIUrl}/wps_ksyun/login?ver=v2&redirect_uri=https://web-game.ked.ksyun.com/login`
  } else {
    window.location.href = `${baseAPIUrl}/wps_ksyun/login?ver=v2&redirect_uri=${siteUrl}/login`
  }

  return new Ok(0)
}

// 从 url 中解析企业登录授权信息
function parseKCLoginParams(params: URLSearchParams): BizResult<KcLoginParam> {
  const error = params.get("error")
  if (error) {
    const errCode = Number.parseInt(params.get("errorCode") ?? "-1")
    // 企业登录错误；errorCode 由授权服务端透传，例如 -10061 表示用户取消企业授权
    DataReportUsecase.reportError(errCode, error)
    return new BizError(errCode)
  }

  //  uuid : 登录接口传入 username
  //  uid : 三方uid,如wps为邮箱前缀(企业登录的用户ID,从授权url中获取(wangliping2这种,是邮箱前缀，主要用作密码校验))
  //  nickname : 昵称(汪丽萍 这种)
  //  hasSetPass: 是否设置过密码 0-否 1-是
  //  passToken : 设置密码临时token,未设置密码时返回,设置密码时需带上此参数
  //  ksAuthCode:  登录token

  // 原先的
  // const token = params.get("token")!
  // const nickname = params.get("nickname")!
  // const username = params.get("username")!
  // const companyUniqID = params.get("companyUniqID")!

  // 新的
  const uuid = params.get("uuid")!
  const uid = params.get("uid")!
  const nickname = params.get("nickname")!
  const hasSetPass = params.get("hasSetPass")!
  const passToken = params.get("passToken")!
  const ksAuthCode = params.get("ksAuthCode")!
  const businessScene = params.get("businessScene")!
  const region = params.get("region ")!
  const desktopId = params.get("desktopId")!

  // 保存用户信息
  let user: User = {
    account: uuid,
    nickName: nickname,
    avatar: "",
    loginType: "KS",
    ksAuthCode,
    uid,
    // 用于数据上报的字段
    business_scene: businessScene,
    region,
    desktopId,
  }

  const cachedUser = UserCache.getUser(uuid)

  if (cachedUser !== null) {
    user = { ...cachedUser, ...user }
  }

  UserCache.saveUser(user)
  UserCache.setCurrentUser(user)

  const kcLoginParamsMap = {
    passToken,
  }
  store.dispatch(setKcLoginParams(kcLoginParamsMap))

  return new Ok({ user, hasSetPass: Number.parseInt(hasSetPass) })
}

interface PCLoginParams {
  user: User
  deviceId: number
  desktopId: string
}

// 从 url 中解析个人用户登录信息
function parsePCParams(params: URLSearchParams): BizResult<PCLoginParams> {
  // 有用到：token、desktopID、username、deviceID   没有用到：expiresAt、userid、type
  // https://web.aipc.ksyun.com/login?token=&desktopID=b-gfrggy3cmnrtqndd-hzcm01&expiresAt=1772098519000&username=zhibo%40zhijin.com&userid=1163&deviceID=922188806&type=PCC&_ts=1772012117140

  const token = params.get("token")!
  const username = params.get("username")!
  const companyUniqID = params.get("companyUniqID")!

  const desktopId = params.get("desktopID")!
  const deviceId = Number.parseInt(params.get("deviceID")!)

  // 需要在url中增加到的（待后端加）
  const nickname = params.get("nickname")!
  const businessStatus = params.get("businessStatus")!
  const region = params.get("regionName")!

  const user: User = {
    account: username,
    nickName: nickname,
    avatar: "",
    loginType: "PC",
    desktopId,
    deviceId: params.get("deviceID") ?? "",
    business_scene: businessStatus,
    region,
  }
  const auth: AuthHeader = {
    token,
    companyUniqID,
  }
  store.dispatch(setAuth(auth))

  return new Ok({ user, desktopId, deviceId })
}

// 处理企业登录
async function processKSLogin(user: User): Promise<BizResult<number>> {
  DataReportUsecase.setUserInfo(user)

  // 上报登录事件
  DataReportUsecase.reportLog("login")

  // 保存为当前用户（含触发动态配置拉取，对齐 android _onLogin）
  setCurrentUser(user)
  UserCache.setCurrentUser(user)

  return new Ok(0)
}

// 获取当前登录的用户
function getCurrentUser(): User | null {
  return curUser
}

function setCurrentUser(user: User) {
  curUser = user
  // 登录成功：清空旧缓存并立即拉取客户端动态配置（对齐 android ClientConfigUseCase.reload）
  ClientConfigUseCase.reload()
}

// 是否为个人用户登录
function isPCLogin() {
  return curUser?.loginType === "PC"
}

// 当断开连接时的回调
function onDisconnect() {
  curUser = null
  // 退出登录 / 断开：停止动态配置周期拉取（对齐 android ClientConfigUseCase.stop）
  ClientConfigUseCase.stop()
}

/**
 * 获取图片验证码
 */
async function getImgCode(): Promise<BizResult<ImgCodeRes>> {
  // 登录请求
  const res = await API.getImgCodeApi()
  if (res instanceof HttpAPIError) {
    DataReportUsecase.reportError(res.code, res.msg)
    console.error("UserUseCase get login imgCode error: ", res)
    return BizError.fromHttpError(res)
  }

  return new Ok(res)
}

/**
 * 企业登录  设置密码
 */
async function setKCPass(password: string): Promise<BizResult<any>> {
  const kcLoginParams = store.getState().login.kcLoginParams
  const { passToken } = kcLoginParams

  const res = await API.setKCPassApi(passToken, password)
  if (res instanceof HttpAPIError) {
    //  -10401: passToken已过期    -10402: passToken无效     -10403: passToken或密码为空        跳转到授权页，重新授权
    if (
      res.code === SET_PASSWORD_TOKEN_EXPIRED ||
      res.code === SET_PASSWORD_TOKEN_INVALID ||
      res.code === SET_PASSWORD_PARAM_EMPTY
    ) {
      const { baseAPIUrl, siteUrl } = getAppConfig()
      if (location.hostname === "web-game.ked.ksyun.com") {
        window.location.href = `${baseAPIUrl}/wps_ksyun/login?ver=v2&redirect_uri=https://web-game.ked.ksyun.com/login`
      } else {
        window.location.href = `${baseAPIUrl}/wps_ksyun/login?ver=v2&redirect_uri=${siteUrl}/login`
      }

      DataReportUsecase.reportError(res.code, res.msg)
      console.error("UserUseCase set kc pass error: ", res)
      return BizError.fromHttpError(res)
    }

    // -10404: 用户不存在
    // -10405: 密码已设置
    // -10007: 后端返回：您设置的密码较弱，请重新设置
    if (
      res.code === SET_PASSWORD_USER_NOT_FOUND ||
      res.code === SET_PASSWORD_ALREADY_SET ||
      res.code === ERR_WEAK_PASSWORD
    ) {
      return BizError.fromHttpError(res)
    }
  }

  return new Ok(res)
}

/**
 * 获取公钥 (用于加密密码)
 */
async function getPublicKey(): Promise<
  BizResult<{ public_key: string; version: number }>
> {
  const res = await API.getPublicKeyApi()

  if (res instanceof HttpAPIError) {
    return BizError.fromHttpError(res)
  }

  return new Ok(res)
}

// 获取壁纸列表
async function getWallpaperList(): Promise<BizResult<WallpaperThemeRes[]>> {
  const res = await API.getWallpaperListApi()

  if (res instanceof HttpAPIError) {
    return BizError.fromHttpError(res)
  }

  return new Ok(res.list)
}

export const UserUsecase = {
  oldAccountLogin,
  newAccountLogin,
  startKSLogin,
  parseKCLoginParams,
  processKSLogin,
  getCurrentUser,
  setCurrentUser,
  onDisconnect,
  parsePCParams,
  isPCLogin,
  getImgCode,
  setKCPass,
  getPublicKey,
  getWallpaperList,
  autoLogin,
  setAutoLogin,
}
